import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { searchParents } from "../../api/parents";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import { toast } from "sonner";

export type GuardianDraft = {
  key: string;
  parentId?: string;
  parent?: any;
  newParent?: {
    firstname: string;
    lastname: string;
    sexe: "Masculin" | "Feminin";
    email?: string;
    number?: string;
    adresse?: string;
    isDeceased: boolean;
    statut: boolean;
  };
  linkId: string;
  linkLabel: string;
};

type Props = {
  guardians: GuardianDraft[];
  linkTypes: { id: string; label: string }[];
  onChange: (guardians: GuardianDraft[]) => void;
};

const emptyParent = {
  firstname: "",
  lastname: "",
  sexe: "Masculin" as const,
  email: "",
  number: "",
  adresse: "",
  isDeceased: false,
  statut: true,
};

type NewParentDraft = {
  firstname: string;
  lastname: string;
  sexe: "Masculin" | "Feminin";
  email: string;
  number: string;
  adresse: string;
  isDeceased: boolean;
  statut: boolean;
};

const parentLabel = (parent: any) =>
  `${parent?.firstname || ""} ${parent?.lastname || ""}`.trim();

const normalizePhone = (value?: string) => (value || "").replace(/\D/g, "").replace(/^00/, "");
const duplicateContact = (parent: any, guardians: GuardianDraft[], excludedKey?: string) => {
  const email = (parent.email || "").trim().toLowerCase();
  const number = normalizePhone(parent.number);
  return guardians.find(guardian => {
    if (guardian.key === excludedKey) return false;
    const other = guardian.newParent || guardian.parent || {};
    return (email && email === (other.email || "").trim().toLowerCase()) ||
      (number && number === normalizePhone(other.number));
  });
};

const StudentGuardiansSection = ({ guardians, linkTypes, onChange }: Props) => {
  const [editingKey, setEditingKey] = useState<string>();
  const editingGuardian = guardians.find(guardian => guardian.key === editingKey);
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState<"search" | "create">("search");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedLinkId, setSelectedLinkId] = useState("");
  const [newParent, setNewParent] = useState<NewParentDraft>({ ...emptyParent });
  const [duplicates, setDuplicates] = useState<any[]>([]);

  const localDuplicate = duplicateContact(newParent, guardians, editingKey);

  const selectedLink = useMemo(
    () => linkTypes.find((link) => link.id === selectedLinkId),
    [linkTypes, selectedLinkId],
  );

  const closeModal = () => {
    setIsSearching(false);
    setShow(false);
    setEditingKey(undefined);
    setMode("search");
    setQuery("");
    setResults([]);
    setDuplicates([]);
    setSelectedLinkId("");
    setNewParent({ ...emptyParent });
  };

  useEffect(() => {
    if (!show || mode !== "search" || query.trim().length < 2) {
      setResults([]);
      return;
    }

    let active = true;
    const timeout = window.setTimeout(async () => {
      try {
        setIsSearching(true);
        const response = await searchParents(
          { q: query.trim(), page: 1, limit: 10 },
          buildPermissionContext(Resource.STUDENT, Action.CREATE),
        );
        if (active) setResults(response.data || []);
      } catch (error: any) {
        if (active) toast.error(error.response?.data?.message || "Recherche de parent impossible");
      } finally {
        if (active) setIsSearching(false);
      }
    }, 400);

    return () => { active = false; window.clearTimeout(timeout); };
  }, [query, mode, show]);

  useEffect(() => {
    if (!show || mode !== "create" || localDuplicate) { setDuplicates([]); setIsSearching(false); return; }
    const email = newParent.email.trim();
    const number = newParent.number.trim();
    if (!email && number.replace(/\D/g, "").length < 6) {
      setDuplicates([]);
      setIsSearching(false);
      return;
    }

    let active = true;
    const timeout = window.setTimeout(async () => {
      try {
        setIsSearching(true);
        const response = await searchParents(
          { email: email || undefined, number: number || undefined, limit: 5 },
          buildPermissionContext(Resource.STUDENT, Action.CREATE),
        );
        if (active) setDuplicates(response.data || []);
      } catch {
        if (active) setDuplicates([]);
      } finally {
        if (active) setIsSearching(false);
      }
    }, 500);

    return () => { active = false; window.clearTimeout(timeout); };
  }, [newParent.email, newParent.number, mode, show, localDuplicate]);

  const addExisting = (parent: any) => {
    if (!selectedLink) {
      toast.error("Choisissez la relation avec l'étudiant");
      return;
    }
    if (guardians.some((guardian) => guardian.key !== editingKey && guardian.parentId === parent.id) || duplicateContact(parent, guardians, editingKey)) {
      toast.info("Ce parent est déjà associé");
      return;
    }
    onChange([
      ...guardians.filter(guardian => guardian.key !== editingKey),
      {
        key: parent.id,
        parentId: parent.id,
        parent,
        linkId: selectedLink.id,
        linkLabel: selectedLink.label,
      },
    ]);
    closeModal();
  };

  const addNew = () => {
    if (!selectedLink) {
      toast.error("Choisissez la relation avec l'étudiant");
      return;
    }
    if (!newParent.firstname.trim() || !newParent.lastname.trim()) {
      toast.error("Le nom et le prénom du parent sont obligatoires");
      return;
    }
    if (newParent.email.trim() && !/^\S+@\S+\.\S+$/.test(newParent.email.trim())) {
      toast.error("L'adresse e-mail du parent est invalide");
      return;
    }
    if (localDuplicate) {
      toast.error("Ce téléphone ou cet e-mail est déjà utilisé par un responsable de cette inscription.");
      return;
    }
    if (duplicates.length > 0) {
      toast.error("Une fiche similaire existe déjà. Associez-la ou modifiez les coordonnées.");
      return;
    }

    const draft: GuardianDraft = {
        key: editingKey || `new-${Date.now()}`,
        newParent: {
          ...newParent,
          firstname: newParent.firstname.trim(),
          lastname: newParent.lastname.trim(),
          email: newParent.email.trim() || undefined,
          number: newParent.number.trim() || undefined,
          adresse: newParent.adresse.trim() || undefined,
        },
        linkId: selectedLink.id,
        linkLabel: selectedLink.label,
    };
    onChange(editingKey ? guardians.map(guardian => guardian.key === editingKey ? draft : guardian) : [...guardians, draft]);
    closeModal();
  };

  const editGuardian = (guardian: GuardianDraft) => {
    setEditingKey(guardian.key);
    setSelectedLinkId(guardian.linkId);
    setDuplicates([]);
    setMode(guardian.newParent ? "create" : "search");
    if (guardian.newParent) setNewParent({ ...emptyParent, ...guardian.newParent, email: guardian.newParent.email || "", number: guardian.newParent.number || "", adresse: guardian.newParent.adresse || "" });
    setShow(true);
  };

  return (
    <div className="card">
      <div className="card-header bg-light d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
            <i className="ti ti-user-shield fs-16" />
          </span>
          <h4 className="text-dark mb-0">Parents et responsables</h4>
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setShow(true)}>
          <i className="ti ti-user-plus me-2" />
          Ajouter un responsable
        </button>
      </div>
      <div className="card-body">
        {guardians.length === 0 ? (
          <p className="text-muted mb-0">Aucun parent ou responsable associé.</p>
        ) : (
          <div className="list-group list-group-flush">
            {guardians.map((guardian) => {
              const parent = guardian.parent || guardian.newParent;
              return (
                <div key={guardian.key} className="list-group-item px-0 d-flex align-items-center justify-content-between gap-3">
                  <div className="min-w-0">
                    <div className="fw-semibold text-dark">{parentLabel(parent)}</div>
                    <div className="text-muted fs-12">
                      {guardian.linkLabel}
                      {parent?.number ? ` · ${parent.number}` : ""}
                      {parent?.email ? ` · ${parent.email}` : ""}
                      {guardian.newParent ? " · Nouveau" : ""}
                    </div>
                  </div>
                  <div className="d-flex gap-2 flex-shrink-0">
                  <button type="button" className="btn btn-sm btn-light" title="Modifier ce responsable" onClick={() => editGuardian(guardian)}>
                    <i className="ti ti-pencil me-1" />Modifier
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    title="Retirer ce responsable"
                    onClick={() => onChange(guardians.filter((item) => item.key !== guardian.key))}
                  >
                    <i className="ti ti-trash" />
                  </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Modal show={show} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingKey ? "Modifier le responsable" : "Ajouter un parent ou responsable"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!editingKey && <div className="btn-group mb-3" role="group" aria-label="Mode d'ajout du parent">
            <button type="button" className={`btn ${mode === "search" ? "btn-primary" : "btn-light"}`} onClick={() => setMode("search")}>
              Rechercher
            </button>
            <button type="button" className={`btn ${mode === "create" ? "btn-primary" : "btn-light"}`} onClick={() => setMode("create")}>
              Créer un parent
            </button>
          </div>}

          <div className="mb-3">
            <label className="form-label">Relation avec l'étudiant</label>
            <select className="form-select" value={selectedLinkId} onChange={(event) => setSelectedLinkId(event.target.value)}>
              <option value="">Choisir une relation</option>
              {linkTypes.map((link) => <option key={link.id} value={link.id}>{link.label}</option>)}
            </select>
          </div>

          {editingGuardian?.parentId ? (
            <div className="alert alert-info">{parentLabel(editingGuardian.parent)} — Vous pouvez modifier la relation ici. Les coordonnées de ce responsable existant se modifient depuis sa fiche.</div>
          ) : mode === "search" ? (
            <>
              <label className="form-label">Nom, téléphone ou e-mail</label>
              <input className="form-control" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher un parent existant" autoFocus />
              <div className="mt-3">
                {isSearching && <div className="text-muted">Recherche...</div>}
                {!isSearching && query.trim().length >= 2 && results.length === 0 && <div className="text-muted">Aucun parent trouvé.</div>}
                {results.map((parent) => (
                  <div key={parent.id} className="border-bottom py-3 d-flex align-items-center justify-content-between gap-3">
                    <div>
                      <div className="fw-semibold">{parentLabel(parent)}</div>
                      <div className="text-muted fs-12">{parent.number || "Sans téléphone"}{parent.email ? ` · ${parent.email}` : ""}</div>
                    </div>
                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => addExisting(parent)}>Associer</button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              {localDuplicate && <div className="alert alert-danger" role="alert">Ce téléphone ou cet e-mail est déjà utilisé par {parentLabel(localDuplicate.newParent || localDuplicate.parent)} dans cette inscription.</div>}
              {duplicates.length > 0 && (
                <div className="alert alert-warning">
                  <div className="fw-semibold mb-2">Une fiche similaire existe déjà.</div>
                  {duplicates.map((parent) => (
                    <div key={parent.id} className="d-flex justify-content-between align-items-center gap-2 py-1">
                      <span>{parentLabel(parent)} · {parent.number || parent.email}</span>
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => addExisting(parent)}>Associer</button>
                    </div>
                  ))}
                </div>
              )}
              <div className="row">
                <div className="col-md-6 mb-3"><label className="form-label">Prénom *</label><input className="form-control" value={newParent.firstname} onChange={(e) => setNewParent({ ...newParent, firstname: e.target.value })} /></div>
                <div className="col-md-6 mb-3"><label className="form-label">Nom *</label><input className="form-control" value={newParent.lastname} onChange={(e) => setNewParent({ ...newParent, lastname: e.target.value })} /></div>
                <div className="col-md-6 mb-3"><label className="form-label">Sexe *</label><select className="form-select" value={newParent.sexe} onChange={(e) => setNewParent({ ...newParent, sexe: e.target.value as "Masculin" | "Feminin" })}><option value="Masculin">Masculin</option><option value="Feminin">Féminin</option></select></div>
                <div className="col-md-6 mb-3"><label className="form-label">Téléphone</label><input className="form-control" value={newParent.number} onChange={(e) => setNewParent({ ...newParent, number: e.target.value })} /></div>
                <div className="col-md-6 mb-3"><label className="form-label">E-mail</label><input type="email" className="form-control" value={newParent.email} onChange={(e) => setNewParent({ ...newParent, email: e.target.value })} /></div>
                <div className="col-md-6 mb-3"><label className="form-label">Adresse</label><input className="form-control" value={newParent.adresse} onChange={(e) => setNewParent({ ...newParent, adresse: e.target.value })} /></div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-light" onClick={closeModal}>Annuler</button>
          {editingGuardian?.parentId && <button type="button" className="btn btn-primary" onClick={() => {
            if (!selectedLink) { toast.error("Choisissez la relation avec l’étudiant"); return; }
            onChange(guardians.map(guardian => guardian.key === editingKey ? { ...guardian, linkId: selectedLink.id, linkLabel: selectedLink.label } : guardian));
            closeModal();
          }}>Enregistrer les modifications</button>}
          {mode === "create" && <button type="button" className="btn btn-primary" disabled={isSearching || duplicates.length > 0 || Boolean(localDuplicate)} onClick={addNew}>{editingKey ? "Enregistrer les modifications" : "Ajouter ce parent"}</button>}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default StudentGuardiansSection;
